import { ReactElement } from 'react';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { ButtonContained, ButtonOutlined } from 'components/general';
import { useLogout } from 'components/core/WithLogout';
import { useAppNavigation } from 'contexts/navigation';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, ProjectSetupTranslationKeys } from 'intl/keys';
import { updateServiceWorkerAndReload } from 'services/serviceWorker/updateServiceWorkerAndReload';
import type { ProjectDataLoadingFailure } from 'store/project';
import { useProjectActions } from 'store/project';
import { getProjectLoadingFailedContent, isChooseProjectAction, ProjectLoadingFailedAction } from './model';
import { ProjectHeading } from './ProjectHeading';
import { actionsSx, contentSx, descriptionSx, headlineSx, rootSx } from './styles';

/**
 * Shown instead of a loading without end: the worker could not load the
 * project's data. Trying again waits for the missing data only; an outdated
 * worker needs the page refreshed instead; a project the user has no access to
 * any more can only be left. The project list is always one click away.
 */
export function ProjectLoadingFailed({ failure }: { failure: ProjectDataLoadingFailure }): ReactElement {
  const intl = useIntl();
  const { retryProjectDataLoading } = useProjectActions();
  const { goToSelectProject } = useAppNavigation();
  const { logout } = useLogout();
  const { titleKey, descriptionKey, primaryAction } = getProjectLoadingFailedContent(failure);

  const primaryButtons: Record<ProjectLoadingFailedAction, ReactElement> = {
    [ProjectLoadingFailedAction.retry]: (
      <ButtonContained onClick={retryProjectDataLoading}>
        {formatTranslation(intl, ProjectSetupTranslationKeys.loadingRetry)}
      </ButtonContained>
    ),
    [ProjectLoadingFailedAction.refresh]: (
      <ButtonContained onClick={() => void updateServiceWorkerAndReload('project-data-loading')}>
        {formatTranslation(intl, CommonTranslationKeys.refresh)}
      </ButtonContained>
    ),
    [ProjectLoadingFailedAction.chooseProject]: (
      <ButtonContained onClick={() => goToSelectProject()}>
        {formatTranslation(intl, ProjectSetupTranslationKeys.loadingChooseProject)}
      </ButtonContained>
    ),
  };

  return (
    <Box sx={rootSx}>
      <Box sx={contentSx} role='alert'>
        <ProjectHeading />
        <Box sx={headlineSx}>{formatTranslation(intl, titleKey)}</Box>
        <Box sx={descriptionSx}>{formatTranslation(intl, descriptionKey)}</Box>
        <Box sx={actionsSx}>
          {primaryButtons[primaryAction]}
          {!isChooseProjectAction(primaryAction) && (
            <ButtonOutlined onClick={() => goToSelectProject()}>
              {formatTranslation(intl, ProjectSetupTranslationKeys.loadingChooseAnotherProject)}
            </ButtonOutlined>
          )}
          <ButtonOutlined onClick={() => logout()}>{formatTranslation(intl, CommonTranslationKeys.logOut)}</ButtonOutlined>
        </Box>
      </Box>
    </Box>
  );
}
